// ============================================================================
// AISettingsPricingSection — editor for ai_model_pricing rows
//
// Drives the cost computation across the App. Super-admins can:
//   • Edit rates inline (LLM: input/output per 1M tokens; non-LLM:
//     per_unit_cents) — auto-saves on blur.
//   • Toggle is_active to hide a model without losing its row.
//   • Add new rows for vendors/models the App starts using.
//   • Delete obsolete rows.
//
// All money is stored as US cents. The LLM input field is dollars-per-1M
// for input ergonomics (much friendlier than typing 500 for $5/1M) and
// converted to cents at save time.
//
// Designed to drop into AISettings.tsx as its own <section>, after the
// last existing section and before the sticky Save row.
// ============================================================================

import { useEffect, useState } from 'react';
import { DollarSign, Loader2, Plus, Save, Trash2, Check, AlertTriangle } from 'lucide-react';
import {
  fetchAiModelPricing,
  updateAiModelPricing,
  createAiModelPricing,
  deleteAiModelPricing,
  type AiModelPricingRow,
} from '@/lib/api';

type Draft = Partial<AiModelPricingRow> & { _localId: string };

/** Convert UI dollars-per-1M-tokens to integer cents-per-1M-tokens. */
function dollarsToCentsPer1m(dollars: string): number | null {
  const n = parseFloat(dollars);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}
function centsPer1mToDollars(cents: number | null | undefined): string {
  if (cents == null) return '';
  return (cents / 100).toFixed(2);
}

export default function AISettingsPricingSection() {
  const [rows, setRows] = useState<AiModelPricingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Per-row local draft state. Cleared back to row values on save.
  // Keyed by id so a slow save on row A doesn't clobber edits on row B.
  const [drafts, setDrafts] = useState<Record<string, Partial<AiModelPricingRow>>>({});

  // New-row draft, lives only while user is composing.
  const [newDraft, setNewDraft] = useState<Draft | null>(null);

  useEffect(() => {
    fetchAiModelPricing()
      .then(setRows)
      .catch(e => setError(e?.message || 'Failed to load pricing'))
      .finally(() => setLoading(false));
  }, []);

  const setField = (id: string, field: keyof AiModelPricingRow, value: any) => {
    setDrafts(d => ({ ...d, [id]: { ...d[id], [field]: value } }));
  };

  const saveRow = async (id: string) => {
    const patch = drafts[id];
    if (!patch || Object.keys(patch).length === 0) return;
    setSavingId(id);
    try {
      const updated = await updateAiModelPricing(id, patch);
      setRows(rs => rs.map(r => r.id === id ? updated : r));
      setDrafts(d => { const c = { ...d }; delete c[id]; return c; });
      setSavedId(id);
      setTimeout(() => setSavedId(p => p === id ? null : p), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingId(null);
    }
  };

  const removeRow = async (id: string) => {
    if (!confirm('Delete this pricing row? The matching feature will log cost=NULL until you add it back.')) return;
    try {
      await deleteAiModelPricing(id);
      setRows(rs => rs.filter(r => r.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const saveNew = async () => {
    if (!newDraft) return;
    const required: Array<keyof AiModelPricingRow> = ['provider', 'model', 'kind'];
    for (const k of required) {
      if (!newDraft[k]) { setError(`Field "${k}" is required.`); return; }
    }
    setAdding(true);
    try {
      const created = await createAiModelPricing({
        provider:                newDraft.provider!,
        model:                   newDraft.model!,
        kind:                    newDraft.kind!,
        input_per_1m_usd_cents:  newDraft.input_per_1m_usd_cents  ?? null,
        output_per_1m_usd_cents: newDraft.output_per_1m_usd_cents ?? null,
        per_unit_cents:          newDraft.per_unit_cents          ?? null,
        flat_fee_cents:          newDraft.flat_fee_cents          ?? null,
        fee_bps:                 newDraft.fee_bps                 ?? null,
        notes:                   newDraft.notes                   ?? null,
        is_active:               newDraft.is_active               ?? true,
      });
      setRows(rs => [...rs, created]);
      setNewDraft(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAdding(false);
    }
  };

  const grouped: Record<string, AiModelPricingRow[]> = {};
  for (const r of rows) {
    const k = r.kind;
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(r);
  }
  const groupOrder: AiModelPricingRow['kind'][] = ['llm', 'image', 'video', 'audio', 'render', 'call', 'fee_bps'];

  return (
    <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-50 text-emerald-700">
            <DollarSign size={18} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-surface-900">Pricing</h2>
            <p className="text-xs text-surface-500 mt-0.5">
              Drives cost computation in the Per-Tenant Cost panel. Edited rows take effect on the next worker cold start (≤ 15 min).
            </p>
          </div>
        </div>
        <button
          onClick={() => setNewDraft({ _localId: 'new', kind: 'llm', is_active: true })}
          disabled={!!newDraft}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          <Plus size={14} /> Add row
        </button>
      </div>

      {error && (
        <div className="mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertTriangle size={14} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-xs underline">dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8 text-surface-400">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <div className="mt-4 space-y-5">
          {groupOrder.map(kind => {
            const group = grouped[kind];
            if (!group?.length) return null;
            return (
              <div key={kind}>
                <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">
                  {kind === 'llm' ? 'LLM (token-priced)' :
                   kind === 'fee_bps' ? 'Fee (flat + bps)' :
                   `${kind.charAt(0).toUpperCase()}${kind.slice(1)} (per unit)`}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-surface-200 text-left text-[11px] uppercase tracking-wide text-surface-500">
                        <th className="py-1.5 pr-2">Provider</th>
                        <th className="py-1.5 pr-2">Model / SKU</th>
                        {kind === 'llm' && (
                          <>
                            <th className="py-1.5 pr-2">$ / 1M in</th>
                            <th className="py-1.5 pr-2">$ / 1M out</th>
                          </>
                        )}
                        {kind !== 'llm' && kind !== 'fee_bps' && <th className="py-1.5 pr-2">¢ / unit</th>}
                        {kind === 'fee_bps' && (
                          <>
                            <th className="py-1.5 pr-2">Flat ¢</th>
                            <th className="py-1.5 pr-2">bps</th>
                          </>
                        )}
                        <th className="py-1.5 pr-2">Notes</th>
                        <th className="py-1.5 pr-2 w-20">Active</th>
                        <th className="py-1.5 pr-2 w-32"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.map(r => {
                        const draft = drafts[r.id] || {};
                        const v = { ...r, ...draft };
                        const isDirty = !!drafts[r.id] && Object.keys(drafts[r.id]).length > 0;
                        return (
                          <tr key={r.id} className="border-b border-surface-100 last:border-b-0">
                            <td className="py-1.5 pr-2 font-mono text-[12px] text-surface-700">{v.provider}</td>
                            <td className="py-1.5 pr-2 font-mono text-[12px] text-surface-700">{v.model}</td>
                            {kind === 'llm' && (
                              <>
                                <td className="py-1.5 pr-2">
                                  <input
                                    type="number" step="0.01" min="0"
                                    className="w-20 px-1.5 py-1 text-[12px] rounded border border-surface-200 bg-surface-0"
                                    value={centsPer1mToDollars(v.input_per_1m_usd_cents)}
                                    onChange={e => setField(r.id, 'input_per_1m_usd_cents', dollarsToCentsPer1m(e.target.value))}
                                  />
                                </td>
                                <td className="py-1.5 pr-2">
                                  <input
                                    type="number" step="0.01" min="0"
                                    className="w-20 px-1.5 py-1 text-[12px] rounded border border-surface-200 bg-surface-0"
                                    value={centsPer1mToDollars(v.output_per_1m_usd_cents)}
                                    onChange={e => setField(r.id, 'output_per_1m_usd_cents', dollarsToCentsPer1m(e.target.value))}
                                  />
                                </td>
                              </>
                            )}
                            {kind !== 'llm' && kind !== 'fee_bps' && (
                              <td className="py-1.5 pr-2">
                                <input
                                  type="number" step="0.0001" min="0"
                                  className="w-24 px-1.5 py-1 text-[12px] rounded border border-surface-200 bg-surface-0"
                                  value={v.per_unit_cents ?? ''}
                                  onChange={e => setField(r.id, 'per_unit_cents', e.target.value === '' ? null : Number(e.target.value))}
                                />
                              </td>
                            )}
                            {kind === 'fee_bps' && (
                              <>
                                <td className="py-1.5 pr-2">
                                  <input
                                    type="number" step="1" min="0"
                                    className="w-20 px-1.5 py-1 text-[12px] rounded border border-surface-200 bg-surface-0"
                                    value={v.flat_fee_cents ?? ''}
                                    onChange={e => setField(r.id, 'flat_fee_cents', e.target.value === '' ? null : Number(e.target.value))}
                                  />
                                </td>
                                <td className="py-1.5 pr-2">
                                  <input
                                    type="number" step="1" min="0"
                                    className="w-20 px-1.5 py-1 text-[12px] rounded border border-surface-200 bg-surface-0"
                                    value={v.fee_bps ?? ''}
                                    onChange={e => setField(r.id, 'fee_bps', e.target.value === '' ? null : Number(e.target.value))}
                                  />
                                </td>
                              </>
                            )}
                            <td className="py-1.5 pr-2">
                              <input
                                type="text"
                                className="w-full px-1.5 py-1 text-[12px] rounded border border-surface-200 bg-surface-0"
                                value={v.notes ?? ''}
                                onChange={e => setField(r.id, 'notes', e.target.value)}
                              />
                            </td>
                            <td className="py-1.5 pr-2">
                              <input
                                type="checkbox"
                                checked={v.is_active}
                                onChange={e => setField(r.id, 'is_active', e.target.checked)}
                              />
                            </td>
                            <td className="py-1.5 pr-2">
                              <div className="flex items-center gap-1.5">
                                {isDirty && (
                                  <button
                                    onClick={() => saveRow(r.id)}
                                    disabled={savingId === r.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary text-white text-[11px] font-semibold hover:bg-primary/90 disabled:opacity-50"
                                  >
                                    {savingId === r.id ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                                    Save
                                  </button>
                                )}
                                {savedId === r.id && (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
                                    <Check size={11} /> Saved
                                  </span>
                                )}
                                <button
                                  onClick={() => removeRow(r.id)}
                                  className="p-1 text-surface-400 hover:text-red-600"
                                  title="Delete row"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {/* Add-row inline composer. Renders right under the table once
              "Add row" is pressed; minimal validation, full inline edit. */}
          {newDraft && (
            <div className="mt-2 p-3 rounded-lg border border-dashed border-primary/40 bg-primary/5 space-y-2">
              <div className="grid grid-cols-3 gap-2 text-[12px]">
                <input className="px-2 py-1 rounded border border-surface-200 bg-surface-0" placeholder="provider" value={newDraft.provider ?? ''} onChange={e => setNewDraft({ ...newDraft, provider: e.target.value })} />
                <input className="px-2 py-1 rounded border border-surface-200 bg-surface-0" placeholder="model / SKU" value={newDraft.model ?? ''} onChange={e => setNewDraft({ ...newDraft, model: e.target.value })} />
                <select className="px-2 py-1 rounded border border-surface-200 bg-surface-0" value={newDraft.kind} onChange={e => setNewDraft({ ...newDraft, kind: e.target.value as AiModelPricingRow['kind'] })}>
                  <option value="llm">llm</option>
                  <option value="image">image</option>
                  <option value="video">video</option>
                  <option value="audio">audio</option>
                  <option value="render">render</option>
                  <option value="call">call</option>
                  <option value="fee_bps">fee_bps</option>
                </select>
              </div>
              {newDraft.kind === 'llm' && (
                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <input type="number" step="0.01" placeholder="$ / 1M input" className="px-2 py-1 rounded border border-surface-200 bg-surface-0"
                    onChange={e => setNewDraft({ ...newDraft, input_per_1m_usd_cents: dollarsToCentsPer1m(e.target.value) })} />
                  <input type="number" step="0.01" placeholder="$ / 1M output" className="px-2 py-1 rounded border border-surface-200 bg-surface-0"
                    onChange={e => setNewDraft({ ...newDraft, output_per_1m_usd_cents: dollarsToCentsPer1m(e.target.value) })} />
                </div>
              )}
              {newDraft.kind && ['image','video','audio','render','call'].includes(newDraft.kind) && (
                <input type="number" step="0.0001" placeholder="¢ per unit" className="px-2 py-1 rounded border border-surface-200 bg-surface-0 text-[12px] w-32"
                  onChange={e => setNewDraft({ ...newDraft, per_unit_cents: e.target.value === '' ? null : Number(e.target.value) })} />
              )}
              <input className="w-full px-2 py-1 rounded border border-surface-200 bg-surface-0 text-[12px]" placeholder="notes (optional)" value={newDraft.notes ?? ''} onChange={e => setNewDraft({ ...newDraft, notes: e.target.value })} />
              <div className="flex gap-2">
                <button onClick={saveNew} disabled={adding} className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
                  {adding ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save row
                </button>
                <button onClick={() => setNewDraft(null)} className="px-3 py-1.5 rounded text-sm text-surface-600 hover:bg-surface-100">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
